import { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Warranty } from '../../types/warranty';
import { Template } from '../../types/template';
import { warrantyToKeyValuePairs } from '../../utils/warrantyDataMapper';
import { DocGenerator } from '../../utils/docGenerator';
import { useStorage } from '../../hooks/useStorage';
import { useTemplates } from '../../hooks/useTemplates';
import toast from 'react-hot-toast';
import { DocumentTextIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import Modal from '../common/Modal';

interface WarrantyDocumentGeneratorProps {
  warranty: Warranty | null;
  isOpen: boolean;
  onClose: () => void;
}

const WarrantyDocumentGenerator: React.FC<WarrantyDocumentGeneratorProps> = ({
  warranty,
  isOpen,
  onClose
}) => {
  const { t } = useLanguage();
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [selectedFormats, setSelectedFormats] = useState<string[]>(['docx']);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedFiles, setGeneratedFiles] = useState<{url: string, fileName: string}[]>([]);
  const { uploadFile } = useStorage();
  const { templates, loading: templatesLoading } = useTemplates();

  // Szűrjük a warranty típusú sablonokat
  const warrantyTemplates = templates.filter(template => template.type === 'warranty');

  // Ha nincs warranty, ne jelenítse meg a modal-t
  if (!warranty) {
    return null;
  }

  const handleGenerateDocument = async () => {
    if (!selectedTemplate || !warranty) {
      toast.error(t('documents.errors.noWarrantyTemplateSelected'));
      return;
    }

    setIsGenerating(true);
    try {
      // Konvertáljuk a warranty adatokat
      const warrantyData = warrantyToKeyValuePairs(warranty);
      
      // Generáljuk a dokumentumot
      const generatedDocs = await DocGenerator.generateDocuments(
        selectedTemplate,
        [warrantyData],
        selectedFormats
      );

      const uploadedFiles: {url: string, fileName: string}[] = [];

      // Feltöltjük a generált fájlokat
      for (const [format, blobs] of Object.entries(generatedDocs)) {
        for (const blob of blobs) {
          const fileName = `${warranty.warrantyNumber}_garanciajegy.${format}`;
          // Konvertáljuk a Blob-ot File objektummá
          const file = new File([blob], fileName, { type: blob.type });
          const url = await uploadFile(file, `warranty-documents/${fileName}`);
          uploadedFiles.push({ url, fileName });
        }
      }

      setGeneratedFiles(uploadedFiles);
      toast.success(t('documents.generatedWarrantyDocumentSuccess'));
    } catch (error) {
      console.error('Error generating warranty document:', error);
      toast.error(t('documents.generatedWarrantyDocumentError'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = (file: {url: string, fileName: string}) => {
    const link = document.createElement('a');
    link.href = file.url;
    link.download = file.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('documents.warrantyDescription')}
      size="2xl"
    >
      <div className="p-6 space-y-6">
        {/* Garancia információk */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">{t('documents.warrantyData')}</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-600 dark:text-gray-400">{t('documents.warrantyNumber')}:</span>
              <p className="text-gray-900 dark:text-gray-100">{warranty.warrantyNumber}</p>
            </div>
            <div>
              <span className="font-medium text-gray-600 dark:text-gray-400">{t('documents.productName')}:</span>
              <p className="text-gray-900 dark:text-gray-100">{warranty.productName}</p>
            </div>
            <div>
              <span className="font-medium text-gray-600 dark:text-gray-400">{t('documents.customerName')}:</span>
              <p className="text-gray-900 dark:text-gray-100">{warranty.customerName}</p>
            </div>
            <div>
              <span className="font-medium text-gray-600 dark:text-gray-400">{t('documents.warrantyEndDate')}:</span>
              <p className="text-gray-900 dark:text-gray-100">{warranty.endDate.toLocaleDateString('hu-HU')}</p>
            </div>
          </div>
        </div>

        {/* Sablon választás */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('documents.selectWarrantyTemplate')} *
          </label>
          {templatesLoading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 dark:border-blue-400"></div>
            </div>
          ) : warrantyTemplates.length === 0 ? (
            <div className="text-center py-8">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" />
              <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                {t('documents.noWarrantyTemplates')}
              </h4>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t('documents.noWarrantyTemplatesDescription')}
              </p>
            </div>
          ) : (
            <select
              value={selectedTemplate?.id || ''}
              onChange={(e) => {
                const template = warrantyTemplates.find(t => t.id === e.target.value);
                setSelectedTemplate(template || null);
              }}
              className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 transition-colors duration-200"
            >
              <option value="">{t('documents.selectWarrantyTemplate')}...</option>
              {warrantyTemplates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Formátum választás */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('documents.chooseOutputFormat')}
          </label>
          <div className="space-y-2">
            {['docx', 'pdf'].map(format => (
              <label key={format} className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedFormats.includes(format)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedFormats([...selectedFormats, format]);
                    } else {
                      setSelectedFormats(selectedFormats.filter(f => f !== format));
                    }
                  }}
                  className="rounded border-gray-300 dark:border-gray-600 text-primary-600 dark:text-primary-400 focus:ring-primary-500 dark:focus:ring-primary-400"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  {format.toUpperCase()}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Generálás gomb */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-gray-800"
          >
            Mégse
          </button>
          <button
            onClick={handleGenerateDocument}
            disabled={!selectedTemplate || isGenerating || selectedFormats.length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 dark:bg-primary-700 border border-transparent rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isGenerating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t('documents.generating')}
              </>
            ) : (
              <>
                <DocumentTextIcon className="h-4 w-4 mr-2" />
                {t('documents.generateDocument')}
              </>
            )}
          </button>
        </div>

        {/* Generált fájlok */}
        {generatedFiles.length > 0 && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">{t('documents.downloadDocuments')}</h4>
            <div className="space-y-2">
              {generatedFiles.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                  <span className="text-sm text-green-800 dark:text-green-400">{file.fileName}</span>
                  <button
                    onClick={() => handleDownload(file)}
                    className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 flex items-center"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                    {t('documents.preview.download')}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default WarrantyDocumentGenerator; 